// Estimate-method terms the installer accepts on the Install tab before getting
// the snippet. `version` must match CURRENT_VERSION in
// serverless/100-api/functions/terms/handler.py: bump both when the wording
// changes, and installers will be asked to accept the new version.
//
// DRAFT wording: to be reviewed by a solicitor before production.
//
// Solar estimates accept SOLAR_TERMS instead (below; use termsFor(product)).
import { METHOD_STATEMENT_SOLAR } from "@widget";

export const TERMS = {
  version: "estimate-method-2026-09-25-2",
  title: "How the calculator estimates heat pump size and price",
  sections: [
    {
      heading: "1. What the estimate is",
      body: [
        "The calculator gives homeowners a quote-stage estimate: a ballpark heat pump size and price range to help them budget. It is not a heat loss survey, a design, or a quotation.",
        "Heat loss is estimated using the method set out in the government's Standard Assessment Procedure (SAP 10.2 and RdSAP 10, published by BRE for the Department for Energy Security and Net Zero), the same methodology used to produce Energy Performance Certificates. It is applied to the measurements and construction details an accredited assessor recorded for the home on the GOV.UK EPC register, and to the homeowner's answers where the RdSAP specification gives a rule for them (for example, insulation added since the certificate).",
        "Default U-values and other inputs are taken unaltered from the published RdSAP 10 and SAP 10.2 tables. Older certificates that don't list each window use the default window area from RdSAP 2012 (version 9.94). Where a home has no usable certificate, the same method is applied to the certificates of similar homes at the same postcode.",
        "Design temperatures: 21 °C inside, and outside the 99.6% value for the nearest location in MCS MIS 3005-D Table 2 (from CIBSE Guide A).",
        "The heat pump size shown is the smallest size you have priced that covers the estimated heat loss. If none of your sizes covers it, or the method can't estimate the home online, no price is shown and the homeowner is offered a survey.",
        "The price range is your price for that size, less the Boiler Upgrade Scheme grant, plus or minus 10%. The Compact calculator deducts £9,000 when the home is heated by oil or LPG (per its EPC or the homeowner's answer) and £7,500 otherwise. The Full width and Hosted page calculators check the homeowner's answers against Ofgem's Boiler Upgrade Scheme guidance and deduct the grant the home appears eligible for (£7,500, or £9,000 for off-gas homes replacing oil or LPG while that uplift is available), and only if the homeowner chooses to include it.",
        "The Full width and Hosted page calculators use this method in full. The Compact calculator currently sizes from the property type and number of bedrooms, and will move to this method.",
      ],
    },
    {
      heading: "2. What it is not",
      body: [
        "The estimate is not compliant with, and must not be described as compliant with, MCS MIS 3005-D or BS EN 12831. It does not replace the room-by-room heat loss calculation and survey that MCS requires before a heat pump is specified or installed.",
        "EPC data can be out of date, can include the assessor's default assumptions, and can contain errors. No warranty is given that it is accurate or complete.",
      ],
    },
    {
      heading: "3. Your responsibilities",
      body: [
        "You will carry out a full heat loss survey and confirm the heat pump size and price with the homeowner before quoting or installing.",
        "You are responsible for the prices, grant amount and wording you enter, and for how you follow up leads.",
        "Grant eligibility shown to homeowners is a guide: you are responsible for confirming eligibility and making any Boiler Upgrade Scheme application.",
        "You will present estimates as a guide only and will not remove or contradict the calculator's statement that the installer confirms the final size and price after a survey.",
      ],
    },
    {
      heading: "4. Liability",
      body: [
        "Estimates are provided as-is. Synvu Limited (xenscope) is not liable for decisions made on the basis of an estimate, including the size or price of any system quoted or installed.",
      ],
    },
  ],
  acceptLabel: "I have read and accept these terms for this calculator on behalf of my company",
} as const;

/** The statement homeowners see behind "How is this estimate worked out?" (one copy, in the widget). */
export { METHOD_STATEMENT as HOMEOWNER_METHOD_STATEMENT } from "@widget";

// ─── Solar estimate terms (docs/12-solar-roof-scan.md §6.6) ──────────────────
// Solar widgets accept their own wording and version, so accepting the heat pump
// terms never covers a solar estimate (and vice versa). `version` must match
// SOLAR_VERSION in serverless/100-api/functions/terms/handler.py: bump both when
// the wording changes. Section 1 is the statement homeowners see, word for word.
//
// DRAFT wording: to be reviewed by a solicitor before production.

/** The shape both sets of terms share (what TermsCard shows). */
export interface TermsDoc {
  version: string;
  title: string;
  sections: readonly { heading: string; body: readonly string[] }[];
  acceptLabel: string;
}

export const SOLAR_TERMS: TermsDoc = {
  version: "solar-method-2026-10-06-3",
  title: "How the solar estimate works out panels, savings and price",
  sections: [
    {
      heading: "1. What homeowners are told",
      body: [METHOD_STATEMENT_SOLAR],
    },
    {
      heading: "2. Where the figures come from",
      body: [
        "The roof's shape, the possible panel positions and each position's yearly yield come from licensed third-party aerial imagery and solar modelling data (a 3D roof model and a shading model). xenquote's servers turn them into the outline, panel layout and figures the homeowner sees. The third-party data is never stored.",
        "Generation is the modelled DC yield for a reference panel, scaled to the panel wattage you enter and multiplied by 0.85 for inverter and system losses. Savings use fixed figures: 26p per kWh used at home, 12p per kWh exported (Smart Export Guarantee), with 50% of generation used at home without a battery, or the share you set with one. The suggested system is the smallest you offer whose yearly generation covers the homeowner's usage (2,700 kWh unless they change it).",
        "The price is your installation price, plus your price per panel times the number of panels, plus the price of the inverter step that covers that system size (plus your battery price if the homeowner adds one). It is shown as a range of plus or minus 10%, each end rounded to £50. Systems larger than your largest inverter step are not offered.",
        "Your prices must include installation and VAT at the rate that applies. Solar panels and batteries fitted to homes in Great Britain are currently zero-rated for VAT until 31 March 2027; you are responsible for updating your prices if the rate changes.",
        "On terraced and semi-detached homes the homeowner marks their own part of the roof, so the split between homes is their judgement.",
      ],
    },
    {
      heading: "3. How your prices are kept",
      body: [
        "Your price list stays on xenquote's servers. It is never sent to the homeowner's browser: they only see the range for the system they are looking at.",
      ],
    },
    {
      heading: "4. What it is not",
      body: [
        "The estimate is not a quotation, a design or a survey. It is not the standard performance estimate required by MCS MIS 3002 and must not be described as one, or as compliant with any MCS standard.",
        "It does not check structural suitability, the electrical installation, planning or listed building restrictions, or grid connection (G98/G99 notification or approval).",
        "Aerial imagery can be several years old, and the solar model can be wrong about roof shape, obstructions and shading. Savings depend on future energy prices, export tariffs and how the home uses electricity. No warranty is given that any figure is accurate or complete.",
      ],
    },
    {
      heading: "5. Your responsibilities",
      body: [
        "You will survey the property and confirm the system, its performance and its price with the homeowner before quoting or installing.",
        "Before any contract you will give the homeowner the MCS performance estimate and a written quotation that meet MCS standards and the Renewable Energy Consumer Code (where you are a member).",
        "You are responsible for the prices, panel, inverter and battery details and wording you enter, for keeping them current, and for how you follow up leads.",
        "You will present estimates as a guide only, will not present savings as guaranteed, and will not remove or contradict the statement that the installer confirms the final system and price after a survey.",
      ],
    },
    {
      heading: "6. Liability",
      body: [
        "Estimates are provided as-is. Synvu Limited (xenscope) is not liable for decisions made on the basis of an estimate, including the size, performance, savings or price of any system quoted or installed.",
      ],
    },
  ],
  acceptLabel: "I have read and accept these terms for this solar estimate on behalf of my company",
};

/** The terms a component accepts: solar estimates have their own. */
export const termsFor = (product: "ashp" | "solar" | undefined): TermsDoc => (product === "solar" ? SOLAR_TERMS : TERMS);
